import React from "react";

import { Button, Alert } from "antd";

import {
  TestConnectionContainer,
  TestResultWrapper,
} from "./EntrataTestConnection.styled";

/**
 * Props interface for EntrataTestConnection component
 */
interface EntrataTestConnectionProps {
  /** Callback function triggered when test connection button is clicked */
  onTestConnection: () => void;
  /** Boolean indicating if connection test is currently in progress */
  isTestingConnection: boolean;
  /** Result object containing success status and message from connection test */
  connectionTestResult: {
    success: boolean;
    message: string;
  } | null;
  /** Boolean to disable the test connection button */
  disabled?: boolean;
}

/**
 * EntrataTestConnection Component
 *
 * A reusable component that provides test connection functionality for Entrata integration.
 * Displays a test connection button and shows feedback based on the connection test result.
 *
 * @param props - Component props
 * @returns JSX element containing test connection UI
 */
const EntrataTestConnection: React.FC<EntrataTestConnectionProps> = ({
  onTestConnection,
  isTestingConnection,
  connectionTestResult,
  disabled = false,
}) => {
  /**
   * Determines the alert type based on connection test result
   */
  const getAlertType = (): "success" | "error" | undefined => {
    if (!connectionTestResult) return undefined;
    return connectionTestResult.success ? "success" : "error";
  };

  /**
   * Gets the appropriate icon for the alert
   */
  const getAlertIcon = () => {
    if (!connectionTestResult) return undefined;
    return connectionTestResult.success ? "✅" : "❌";
  };

  /**
   * Gets the formatted message for the alert
   */
  const getFormattedMessage = () => {
    if (!connectionTestResult) return "";
    
    if (connectionTestResult.success) {
      return "Connection successful. VERO is authorized to access your Entrata data. Click 'Enable Integration' to finish setup.";
    } else {
      return "We couldn't connect. Please confirm VERO is added as an API User in your Entrata Admin Portal and that the correct permissions are enabled.";
    }
  };

  return (
    <TestConnectionContainer>
      <div className="entrata-test-connection">
        <Button
          type="default"
          data-testid="test-connection-btn"
          className="test-connection-button"
          disabled={disabled || isTestingConnection}
          loading={isTestingConnection}
          onClick={onTestConnection}
          size="large"
        >
          {isTestingConnection ? "Testing Connection..." : "Test Connection"}
        </Button>
        
        <p className="test-connection-hint">
          This will verify your Entrata company URL and confirm VERO is authorized as an API User.
        </p>

        {/* Connection Test Result Feedback */}
        {connectionTestResult && (
          <TestResultWrapper>
            <Alert
              message={getFormattedMessage()}
              type={getAlertType()}
              icon={getAlertIcon()}
              showIcon
              data-testid="connection-test-result"
            />
          </TestResultWrapper>
        )}
      </div>
    </TestConnectionContainer>
  );
};

export default EntrataTestConnection;
